'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Building2,
  HardHat,
  BarChart3,
  FileText,
  ClipboardList,
  Receipt,
  ShieldCheck,
  ArrowRight,
  IndianRupee,
  Users,
  FolderKanban,
  Lock,
  ChevronRight,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const MODULES = [
  {
    icon: BarChart3,
    title: 'Financial Dashboard',
    desc: 'Live KPIs — total revenue, outstanding AR/AP, cash flow, and profit margins across all active projects.',
    href: '/dashboard',
    color: 'bg-emerald-50 text-emerald-700',
  },
  {
    icon: FolderKanban,
    title: 'Project Control Center',
    desc: 'Kanban board + project table for Building, Interior, and Construction projects. Track status, budget, and milestones.',
    href: '/project-control-center',
    color: 'bg-blue-50 text-blue-700',
  },
  {
    icon: Receipt,
    title: 'Cost & Expense Tracker',
    desc: 'Log material, labour, subcontractor, permit, and overhead costs. Upload receipts directly from site.',
    href: '/cost-expense-tracker',
    color: 'bg-amber-50 text-amber-700',
  },
  {
    icon: FileText,
    title: 'AR / AP Billing',
    desc: 'Raise GST-compliant client invoices (AR) and manage vendor/contractor bills (AP) with approval workflows.',
    href: '/ar-ap-billing',
    color: 'bg-violet-50 text-violet-700',
  },
  {
    icon: ClipboardList,
    title: 'Daily Progress Reports',
    desc: 'Mobile-optimised DPR form for site supervisors — log labour count, material deliveries, and site photos each day.',
    href: '/daily-progress-reports',
    color: 'bg-orange-50 text-orange-700',
  },
  {
    icon: IndianRupee,
    title: 'Financial Analytics',
    desc: 'Visual charts: Estimated vs Actual costs, cash flow trends, profit margins, and AR aging analysis.',
    href: '/financial-analytics-dashboard',
    color: 'bg-teal-50 text-teal-700',
  },
];

const STATS = [
  { value: '3+', label: 'Active Projects' },
  { value: '₹2.4Cr', label: 'Budget Tracked' },
  { value: '3', label: 'User Roles' },
  { value: '6', label: 'Core Modules' },
];

export default function HeroPage() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans">
      {/* Top Nav */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Building2 size={16} className="text-white" />
            </div>
            <div className="leading-none">
              <span className="font-bold text-foreground text-sm">JBW ERP</span>
              <p className="text-[10px] text-muted-foreground">Jangra Buildwave</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
              <Lock size={11} />
              Internal Use Only
            </div>
            <Link
              href="/sign-up-login"
              className="btn-primary text-xs py-2 px-4"
            >
              Sign In <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </header>
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 rounded-full translate-y-1/2 -translate-x-1/3" />
        </div>

        <div className="max-w-6xl mx-auto relative">
          <div className="max-w-3xl">
            {/* Internal badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/8 border border-primary/20 text-primary text-xs font-semibold mb-6">
              <ShieldCheck size={13} />
              JBW Internal Team — Restricted Access
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-foreground leading-[1.1] tracking-tight mb-5">
              One platform to run<br />
              <span className="text-primary">every JBW project.</span>
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              JBW ERP is the internal operations hub for <strong className="text-foreground">Jangra Buildwave Pvt Ltd</strong> — built to manage building, interior design, and construction projects from Bhiwadi to Neemrana. Track budgets, log daily site progress, raise GST invoices, and monitor cash flow — all in one place.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link href="/sign-up-login" className="btn-primary py-3 px-6 text-sm">
                Access the ERP <ArrowRight size={15} />
              </Link>
              <a href="#modules" className="btn-secondary py-3 px-6 text-sm">
                Explore Modules <ChevronRight size={15} />
              </a>
            </div>
          </div>

          {/* Stats row */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {STATS?.map((stat) => (
              <div key={stat?.label} className="card p-4">
                <p className="text-2xl font-bold text-primary tabular-nums">{stat?.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{stat?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* What it does */}
      <section className="py-14 px-4 sm:px-6 bg-muted/40 border-y border-border">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Built for JBW's operations</h2>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              From the moment a project is won to the final payment received — JBW ERP covers the full lifecycle. Site supervisors log daily progress on mobile, project managers track budgets and raise invoices, and the admin team gets a real-time financial picture across every active site.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="card p-5 sm:col-span-1">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <HardHat size={20} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Site Supervisors</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Submit Daily Progress Reports from the construction site using a mobile-optimised form. Log labour headcount, material deliveries, bottlenecks, and upload site photos — no laptop needed.
              </p>
            </div>
            <div className="card p-5 sm:col-span-1">
              <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center mb-4">
                <FolderKanban size={20} className="text-accent" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Project Managers</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Create and manage projects on the Kanban board, assign tasks, log material and labour expenses, and raise client invoices with GST. Keep every project's budget vs. actual spend in check.
              </p>
            </div>
            <div className="card p-5 sm:col-span-1">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Users size={20} className="text-blue-600" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Admin / Owners</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Full access to financial analytics, payroll, AR/AP approvals, and all project data. Monitor cash flow, outstanding balances, and profit margins across the entire business in real time.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Modules */}
      <section id="modules" className="py-14 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Six core modules</h2>
            <p className="text-muted-foreground text-sm sm:text-base">
              Each module is purpose-built for JBW's construction and interior design workflows.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULES?.map((mod) => {
              const Icon = mod?.icon;
              return (
                <Link
                  key={mod?.href}
                  href={mod?.href}
                  className="card p-5 hover:shadow-md transition-shadow group flex flex-col gap-3"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${mod?.color}`}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm mb-1 group-hover:text-primary transition-colors">
                      {mod?.title}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{mod?.desc}</p>
                  </div>
                  <div className="mt-auto flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                    Open module <ArrowRight size={12} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      {/* Access notice */}
      <section className="py-12 px-4 sm:px-6 bg-primary">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
              <Lock size={18} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-lg mb-1">Restricted to JBW team members</h3>
              <p className="text-white/65 text-sm leading-relaxed max-w-xl">
                This application is strictly for internal use by Jangra Buildwave Pvt Ltd employees and contractors. Unauthorised access is prohibited. If you need an account, contact your admin.
              </p>
            </div>
          </div>
          <Link
            href="/sign-up-login"
            className="flex-shrink-0 flex items-center gap-2 bg-white text-primary font-semibold text-sm px-5 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            Sign In to ERP <ArrowRight size={15} />
          </Link>
        </div>
      </section>
      {/* Footer */}
      <footer className="py-6 px-4 sm:px-6 border-t border-border bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>© 2026 Jangra Buildwave Pvt Ltd · Bhiwadi, Rajasthan</span>
          <span className="flex items-center gap-1.5">
            <Lock size={11} />
            Internal use only · Not for public distribution
          </span>
        </div>
      </footer>
    </div>
  );
}